import { Directive, ElementRef, NgZone, OnDestroy, effect, inject } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';

/**
 * Keeps an `appendTo`-ed ng-select dropdown glued to its input while the page scrolls.
 *
 * With `[appendTo]="'body'"` ng-select measures the input once (on open) and positions the
 * panel in body coordinates. It only listens to scrolls inside the panel itself, so scrolling
 * any ancestor container (the dashboard layout's `overflow-y-auto` wrapper, a table, a modal)
 * leaves the panel floating at the old spot.
 *
 * On every outside scroll we re-measure and move the panel; once the input is scrolled out of
 * its scroll container we close the dropdown instead of following it off-screen.
 * No-op when `appendTo` is not set — an inline panel scrolls with the input on its own.
 */
@Directive({
  selector: 'ng-select',
  standalone: true,
})
export class SelectDropdownPositionDirective implements OnDestroy {
  private select = inject(NgSelectComponent, { host: true });
  private el = inject(ElementRef<HTMLElement>);
  private zone = inject(NgZone);

  private listening = false;
  private frame: number | null = null;

  constructor() {
    effect(() => {
      const is_open = this.select.isOpen();
      if (is_open && this.select.appendTo()) {
        this.listen();
      } else {
        this.unlisten();
      }
    });
  }

  ngOnDestroy(): void {
    this.unlisten();
  }

  private listen() {
    if (this.listening) return;
    this.listening = true;
    // capture: true so we also receive scroll events from ancestor containers (scroll does not bubble)
    this.zone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
    });
  }

  private unlisten() {
    if (!this.listening) return;
    this.listening = false;
    window.removeEventListener('scroll', this.onScroll, true);
    window.removeEventListener('resize', this.onScroll);
    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  private onScroll = (event: Event) => {
    const panel = this.panel_element();
    // Scrolling the options list itself must not move or close the dropdown
    if (panel && event.target instanceof Node && panel.contains(event.target)) return;

    const container = event.target instanceof Element ? event.target : null;
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (this.is_hidden_in(container)) {
        this.zone.run(() => this.select.close());
      } else {
        this.reposition();
      }
    });
  };

  private panel_element(): HTMLElement | null {
    const panel = this.select.dropdownPanel();
    return panel ? (panel as any)._dropdown ?? null : null;
  }

  /** True once the input has scrolled out of the container that just scrolled (or the viewport). */
  private is_hidden_in(container: Element | null): boolean {
    const select = this.el.nativeElement.getBoundingClientRect();
    const clip = container && container !== document.scrollingElement && container.getBoundingClientRect
      ? container.getBoundingClientRect()
      : { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth } as DOMRect;

    return select.bottom <= clip.top || select.top >= clip.bottom
      || select.right <= clip.left || select.left >= clip.right;
  }

  private reposition() {
    const dropdown = this.select.dropdownPanel();
    const panel = this.panel_element();
    if (!dropdown || !panel?.parentElement) return;

    // Y (respects the top/bottom flip ng-select picked when it opened)
    dropdown.adjustPosition();

    // X — vertical scrolling never changes it, horizontal scrolling inside a table does
    const select = this.el.nativeElement.getBoundingClientRect();
    const parent = panel.parentElement.getBoundingClientRect();
    if (document.documentElement.dir === 'rtl') {
      panel.style.right = parent.right - select.right + 'px';
      panel.style.left = 'auto';
    } else {
      panel.style.left = select.left - parent.left + 'px';
      panel.style.right = 'auto';
    }
    panel.style.width = select.width + 'px';
    panel.style.minWidth = select.width + 'px';
  }
}
